/**
 * UpdatedStamp — Phase 10.5 visible "Updated {date}" stamp on PDPs.
 *
 * Reads the cached fin_freshness.lastmod (passed in as `lastmod`). Renders a
 * quiet timestamp under the H1 so visitors see at-a-glance how recently we
 * refreshed the product record.
 *
 * Render rules (Real-or-Hidden Rule 3 — never invent freshness):
 *   - lastmod missing or unparseable → render NOTHING (no fallback to NOW()).
 *   - lastmod within the last 30 days → render NOTHING. The plan note: a
 *     freshly-bumped timestamp on every PDP would be misleading clutter; only
 *     show the stamp when the data is stable enough that "Updated {month
 *     year}" is genuinely useful information for the visitor.
 *   - lastmod older than 30 days → render "Updated {Month YYYY}".
 *
 * No emojis. Brand voice: "Fridge.com" only when the brand is named.
 */

interface UpdatedStampProps {
  lastmod?: string | null;
  /**
   * The current time in milliseconds, supplied by the SERVER (FRIDGE-419,
   * react-compiler/purity).
   *
   * This component used to call `Date.now()` in its own render body. It has no
   * "use client" directive, which reads like a server component where that
   * would be harmless — but app/products/[slug]/product-client.tsx imports it,
   * so it ships to the browser and renders there too. A clock read during
   * render gives the server one value and the client another.
   *
   * Taking it as a prop means server and client agree by construction, and the
   * 30-day decision is made against the moment the page was rendered rather
   * than the moment a cached page happened to be hydrated.
   */
  nowMs: number;
  className?: string;
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function UpdatedStamp({ lastmod, nowMs, className = '' }: UpdatedStampProps) {
  if (!lastmod) return null;
  const parsed = new Date(lastmod);
  if (Number.isNaN(parsed.getTime())) return null;

  const ageMs = nowMs - parsed.getTime();
  // Hide when fresher than 30 days — per plan, displaying a current-month
  // timestamp on every PDP every day implies daily curation we don't actually
  // do. Only stable timestamps go on the page.
  if (ageMs < THIRTY_DAYS_MS) return null;
  // Defensive future-date guard — never render a stamp from a date in the future.
  if (ageMs < 0) return null;

  const monthLabel = parsed.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <p
      className={`text-xs text-gray-500 ${className}`}
      title={`Last refreshed ${parsed.toUTCString()} (Fridge.com data sources)`}
    >
      Updated {monthLabel}
    </p>
  );
}

export default UpdatedStamp;
